import * as React from 'react';
import { useLayoutEffect } from 'react';

import { PORTAL_IDS } from 'shared/entities/app';
import { useEventListener } from 'shared/entities/common/hooks';

export type PopupTriggerAttachmentResult = {
  /** доп. стили для content — зарезервировано под будущее */
  attachmentStyle?: React.CSSProperties;
};

type Params = {
  attachToTrigger: boolean;
  opened: boolean;
  reposition: () => void;
  close: () => void;
  repositionDeps?: React.DependencyList;
  isMobile?: boolean;
  triggerRef?: React.RefObject<HTMLElement>;
};

export const usePopupTriggerAttachment = ({
  attachToTrigger,
  opened,
  reposition,
  close,
  repositionDeps = [],
  isMobile = false,
  triggerRef
}: Params): PopupTriggerAttachmentResult => {
  const handleScroll = React.useCallback(
    (event: Event) => {
      if (!opened) {
        return;
      }

      if (attachToTrigger) {
        reposition();
        return;
      }

      if (isMobile) {
        return;
      }

      const target = event.target;

      if (!(target instanceof Node)) {
        return;
      }

      const popupsPortal = document.getElementById(PORTAL_IDS.POPUPS);

      if (popupsPortal?.contains(target)) {
        // scroll внутри popup, в котором лежит наш trigger: popup едет за
        // триггером, иначе он остаётся висеть на прежних координатах
        if (triggerRef?.current && target.contains(triggerRef.current)) {
          reposition();
        }

        return;
      }

      // закрываем popup, если scroll не внутри popup (и не scroll в input)
      if (!(target instanceof HTMLElement && target.nodeName === 'INPUT')) {
        close();
      }
    },
    [attachToTrigger, close, isMobile, opened, reposition, triggerRef]
  );

  useEventListener({
    eventName: 'scroll',
    handler: handleScroll,
    getElement: () => document,
    options: true
  });

  useLayoutEffect(() => {
    if (!attachToTrigger || !opened) {
      return;
    }

    reposition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachToTrigger, opened, reposition, ...repositionDeps]);

  return {};
};
